import { ProofResultInsufficientReasonEnum } from 'src/ts/enums/proof';

export type FailBehavior = 'EDITED' | 'INSUFFICIENT_CONTINUE';

export interface OfferLetterChecklistStep {
  /** Stable semantic key for sessionStorage and state management */
  id: string;
  /** Display label */
  label: string;
  /** Analyst guidance tooltip */
  criteria: string;
  /** What happens when this step fails */
  failBehavior: FailBehavior;
  /** Reason codes auto-mapped on failure */
  reasonCodes?: ProofResultInsufficientReasonEnum[];
}

// UI-only checklist guidance for analysts — not persisted to backend.
// Checklist decisions are not stored; the verification endpoint captures
// the outcome (employer details + contacts) rather than individual check results.
// ITL-3021: field-validation steps removed — the threat-list check is the only
// remaining analyst step. Fail paths: threat_list → EDITED; contacts review
// outcomes (NO_CONTACTS_AVAILABLE / NO_VERIFIED_CONTACTS) are computed in the panel.
export const OFFER_LETTER_CHECKLIST_STEPS: OfferLetterChecklistStep[] = [
  {
    id: 'threat_list',
    label: 'Threat List Check',
    criteria: 'Company is NOT on the Snappt Company Threatlist',
    failBehavior: 'EDITED'
  }
];

// fieldValues key → GET /v2/offer_letter/verifications response key.
// The submit payload builder reads fieldValues by these keys, so the AI seed
// must populate all of them even though most are never displayed (ITL-3021).
export const OLV_LLM_FIELD_MAP = {
  company_name: 'employer_name',
  employee_name: 'employee_name',
  employee_title: 'employee_title',
  address: 'employer_address',
  website: 'employer_website',
  start_date: 'start_date'
} as const;

// Shared key union so the required-fields list and the hook's blank-field
// tracking can't drift from the map — a typo'd key fails to compile instead of
// silently dropping a field from rendering/gating.
export type OlvFieldKey = keyof typeof OLV_LLM_FIELD_MAP;

// The pass submit requires these fieldValues keys non-blank: employer_name and
// employee_name are hard-required by the backend changeset; employee_title is
// required by the Trigo VoE request (job_title). When the AI extraction left one
// blank, the panel renders it as a required input for the analyst to fill.
export const OLV_REQUIRED_FIELDS = [
  { key: 'company_name', label: 'Company Name' },
  { key: 'employee_name', label: 'Employee Name' },
  { key: 'employee_title', label: 'Employee Title' }
] as const satisfies ReadonlyArray<{ key: OlvFieldKey; label: string }>;
